import React from 'react';

interface ClearDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isClearing: boolean;
  restaurantName?: string;
  type: 'revenue' | 'orders';
}

const ClearDataModal: React.FC<ClearDataModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isClearing,
  restaurantName,
  type
}) => {
  if (!isOpen) return null;

  const isRevenue = type === 'revenue';
  const title = isRevenue ? 'Confirm Clear Revenue' : 'Confirm Clear Orders';
  const description = isRevenue
    ? `Selected revenue history data for ${restaurantName || 'the selected restaurants'} will be permanently deleted.`
    : `Selected order history data for ${restaurantName || 'the selected restaurants'} will be permanently deleted.`;
  const buttonText = isRevenue ? 'Clear Revenue' : 'Clear Orders';
  const buttonColorClasses = isRevenue
    ? 'bg-orange-600 hover:bg-orange-700 dark:bg-orange-600 dark:hover:bg-orange-700'
    : 'bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700';
  const gradientColors = isRevenue
    ? 'from-orange-50 to-yellow-50 dark:from-orange-950/20 dark:to-yellow-950/20 border-orange-100 dark:border-orange-900/30'
    : 'from-red-50 to-orange-50 dark:from-red-950/20 dark:to-orange-950/20 border-red-100 dark:border-red-900/30';
  const iconColors = isRevenue
    ? 'bg-orange-500/10 dark:bg-orange-500/20 text-orange-600 dark:text-orange-500'
    : 'bg-red-500/10 dark:bg-red-500/20 text-red-600 dark:text-red-500';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 dark:bg-black/50"
      onClick={() => !isClearing && onClose()}
    >
      <div
        className="bg-card dark:bg-card-dark rounded-3xl shadow-2xl w-full max-w-md border border-zinc-200 dark:border-white/20"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 md:p-8">
          <div className="flex items-start justify-between mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-zinc-900 dark:text-zinc-50">
              {title}
            </h2>
            <button
              onClick={onClose}
              disabled={isClearing}
              className="flex-none p-2 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 transition disabled:opacity-50"
              aria-label="Close"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
              </svg>
            </button>
          </div>

          <div className={`mb-6 p-4 rounded-xl bg-gradient-to-br ${gradientColors} border`}>
            <div className="flex gap-3">
              <div className="flex-none mt-0.5">
                <div className={`h-8 w-8 rounded-lg ${iconColors} grid place-items-center`}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                </div>
              </div>
              <div>
                <p className="text-sm text-zinc-900 dark:text-zinc-100 font-medium mb-1">This action cannot be undone</p>
                <p className="text-xs text-zinc-700 dark:text-zinc-300 leading-relaxed">
                  {description}
                </p>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isClearing}
              className="flex-1 px-6 py-3 rounded-2xl text-base font-semibold border border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isClearing}
              className={`flex-1 px-6 py-3 rounded-2xl text-base font-semibold text-white dark:text-white transition disabled:opacity-50 ${buttonColorClasses}`}
            >
              {isClearing ? 'Clearing...' : buttonText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClearDataModal;
